import { ApplicationApiTagTypes, ApplicationApiUrls } from "@/modules/application/common/enum";
import type {
  IApplicationSignBody,
  IApplicationsRequest,
  IApplicationsResponse,
  IBankBranch,
  IChangeStateRequest,
} from "@/modules/application/common/types";
import { buildRoute, generateQuery } from "@/shared/helpers";
import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "app/axios";

export const applicationApi = createApi({
  reducerPath: "applicationApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: [ApplicationApiTagTypes.ApplicationHistory],
  endpoints: (builder) => ({
    getBankBranches: builder.query<IBankBranch[], void>({
      query: () => ({
        url: ApplicationApiUrls.BankBranch,
      }),
    }),
    getApplications: builder.query<
      IApplicationsResponse,
      IApplicationsRequest
    >({
      query: ({
        pageIndex,
        pageSize,
        applicationType,
        applicationStatus,
        startDate,
        endDate,
        minAmount,
        maxAmount,
        currency,
      }) => {
        const query = generateQuery({
          pageIndex,
          pageSize,
          applicationType,
          applicationStatus,
          startDate,
          endDate,
          minAmount,
          maxAmount,
          currency,
        });
        const url = `${ApplicationApiUrls.Applications}?${query}`;
        return { url };
      },
      providesTags: [ApplicationApiTagTypes.ApplicationHistory],
    }),
    getApplicationNumber: builder.query<string, void>({
      query: () => ({
        url: ApplicationApiUrls.ApplicationNumber,
      }),
    }),
    getApplicationTypes: builder.query<string[], void>({
      query: () => ({
        url: ApplicationApiUrls.ApplicationTypes,
      }),
    }),
    signApplication: builder.mutation<void, IApplicationSignBody>({
      query: (data) => ({
        url: ApplicationApiUrls.ApplicationSign,
        method: "POST",
        data,
      }),
    }),
    changeApplicationState: builder.query<void, IChangeStateRequest>({
      query: ({ data, id }) => {
        const url = buildRoute(ApplicationApiUrls.ApplicationState, { id });
        return {
          url,
          method: "PATCH",
          data,
        };
      }
    }),
  }),
});

export const {
  useGetApplicationsQuery,
  useSignApplicationMutation,
  useLazyGetBankBranchesQuery,
  useLazyGetApplicationNumberQuery,
  useGetApplicationTypesQuery,
  useLazyChangeApplicationStateQuery,
} = applicationApi;
